import { SourcesTooltip } from '@src/entities/Source/ui/components/tooltips/Sources';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Table as TableComponent } from '@src/shared/ui/components/TableQuery';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { orderBy } from 'lodash';
import React, { useMemo } from 'react';

import configuration from './includes/configuration.json';

/**
 * Table: Actions
 *
 * Displays a list of actions within a DataTable.
 *
 * @param error
 * @param isLoading
 * @param requestArgs
 * @param requestFunction
 * @param requestKey
 * @param rows
 * @param totalPages
 * @param totalRecords
 * @returns {Element}
 * @constructor
 */
export function Actions({
  error = null,
  isLoading = false,
  requestArgs = {},
  requestFunction = () => {},
  requestKey = '',
  rows = [],
  totalPages = 1,
  totalRecords = 0,
}) {
  /**
   * renderCellLastUpdated
   *
   * Displays the date and time of when the action was last updated within a cell.
   *
   * @param row
   * @param cell
   * @returns {JSX.Element|*}
   */
  const renderCellLastUpdated = ({ cell }) => {
    return dayjs(cell?.value).format('MM/DD/YYYY, HH:mm:ss');
  };

  /**
   * renderCellStatus
   *
   * Displays the current status of the action within a cell.
   *
   * @param row
   * @param cell
   * @returns {JSX.Element|*}
   */
  const renderCellStatus = ({ cell }) => {
    if (cell?.value === true) {
      return (
        <Badge
          icon={<IconSymbol color="$success1" icon="check_circle" size="$sm" variant="filled" />}
          outline
          variant="success"
        >
          Valid
        </Badge>
      );
    }
    return (
      <Badge
        icon={<IconSymbol color="$error1" icon="error_outline" size="$sm" variant="filled" />}
        outline
        variant="error"
      >
        Invalid
      </Badge>
    );
  };

  /**
   * renderCellAllowedSources
   *
   * Displays a concatenated/truncated list of allowed sources within a cell.
   *
   * @param row
   * @param cell
   * @returns {JSX.Element|*}
   */
  const renderCellAllowedSources = ({ cell, row }) => {
    if (row?.original?.allowableSystems?.length > 1) {
      const sources = row?.original?.allowableSystems;

      return (
        <Tooltip content={<SourcesTooltip sources={sources} />} placement="auto">
          <span className="truncate">{sources.join(', ')}</span>
        </Tooltip>
      );
    }

    return cell.value;
  };

  /**
   * Columns for table.
   */
  const columns = useMemo(() => {
    return orderBy(configuration?.initialColumns, ['order'], ['asc']).map((item) => {
      const column = item;

      if (column.Header === 'Last Updated') {
        column.Cell = renderCellLastUpdated;
      }

      if (column.Header === 'Status') {
        column.Cell = renderCellStatus;
      }

      if (column.Header === 'Allowed Source(s)') {
        column.Cell = renderCellAllowedSources;
      }

      return column;
    });
  }, []);

  return (
    <ErrorHandler location="src/entities/Action/ui/components/tables/Actions/Actions.jsx">
      <TableComponent
        {...{
          columns,
          configuration,
          error,
          isLoading,
          requestArgs,
          requestFunction,
          requestKey,
          rows,
          totalPages,
          totalRecords,
        }}
      />
    </ErrorHandler>
  );
}
